import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { ComboboxField, type ComboboxOption } from '@/components/combobox-field';
import { formatOrderCounterparties } from '@/features/orders/components/orders-table-formatters';
import { useOrderSearch, type OrderSearchHit } from '../api/order-search';
import type { RaiseFormValues } from '../lib/raise-form-schema';

// Searchable order picker for the raise dialog (launchpad drawer parity).
export function OrderComboboxField({
  currentId,
  disabled,
}: Readonly<{
  currentId: string;
  disabled: boolean;
}>) {
  const [search, setSearch] = useState('');
  // Debounced so keystrokes don't each hit /orders (300ms, as elsewhere).
  const { data, isLoading } = useOrderSearch(useDebouncedValue(search, 300));
  const options = useMemo(() => buildOptions(data ?? [], currentId), [data, currentId]);
  return (
    <ComboboxField<RaiseFormValues>
      name="orderId"
      label="Order"
      className="sm:col-span-2"
      options={options}
      placeholder="Search order…"
      searchPlaceholder="Order id or counterparty…"
      emptyMessage="No matching orders."
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      loading={isLoading}
      disabled={disabled}
    />
  );
}

function buildOptions(hits: readonly OrderSearchHit[], currentId: string): ComboboxOption[] {
  const options = hits.map(toOption);
  // Keep the selected order visible even if the search no longer returns it.
  if (currentId !== '' && !options.some((o) => o.value === currentId)) {
    options.unshift({ value: currentId, label: currentId });
  }
  return options;
}

function toOption(hit: OrderSearchHit): ComboboxOption {
  return {
    value: hit.id,
    label: `${hit.id} – ${hit.commodity ?? '—'}`,
    description: formatOrderCounterparties(hit),
  };
}
